import React, { useState, useEffect, useMemo } from 'react';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import './MyPicksNavBar.scss';  // Reusing the same SCSS file

/**
 * SelectionBar - A reusable component for selecting items (teams or events) from a dropdown with arrow navigation
 * @param {Object} props
 * @param {number|null|string} props.selectedId - Currently selected item ID
 * @param {Array} props.items - Array of items (teams or events)
 * @param {string} props.idKey - Property name for the ID field in items (e.g., 'teamId', 'eventId')
 * @param {string} props.nameKey - Property name for the display name in items (e.g., 'teamName', 'eventName')
 * @param {string} props.urlParamKey - URL parameter key to use (e.g., 'teamId', 'eventId')
 * @param {Function} props.onChange - Optional callback when selection changes
 * @param {string} props.backgroundColor - Optional background color override
 * @param {string} props.textColor - Optional text color override
 */
const SelectionBar = ({ 
    selectedId, 
    items = [], 
    idKey = 'id',
    nameKey = 'name',
    urlParamKey = 'id',
    onChange,
    backgroundColor = '#07356B',
    textColor = '#ffffff'
}) => {
    const {
        masterPoolId,
        gameId,
        gameAddress,
        page = 'teams',
    } = useUserAndGameContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Create a memoized mapping of id -> index for quick lookups
    const itemIndexMap = useMemo(() => {
        const map = {};
        items.forEach((item, index) => {
            if (item && item[idKey] !== undefined && item[idKey] !== null) {
                map[item[idKey]] = index;
            }
        });
        return map;
    }, [items, idKey]);
    
    // Determine the currently selected item index
    const currentItemIndex = useMemo(() => {
        if (selectedId !== undefined && selectedId !== null && itemIndexMap[selectedId] !== undefined) {
            return itemIndexMap[selectedId];
        }
        
        // Otherwise check URL params
        const idParam = searchParams.get(urlParamKey);
        if (idParam && itemIndexMap[idParam] !== undefined) {
            return itemIndexMap[idParam];
        }
        
        // Default to first item
        return items.length > 0 ? 0 : -1;
    }, [selectedId, itemIndexMap, searchParams, urlParamKey, items.length]);
    
    // Get the actual item object based on current index
    const currentItem = useMemo(() => {
        return currentItemIndex >= 0 ? items[currentItemIndex] : null;
    }, [items, currentItemIndex]);

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    };

    // Update URL when selection changes
    const updateSelection = (newId) => {
        if (newId === undefined || newId === null) {
            console.error("Cannot update selection with undefined or null ID");
            return;
        }
        
        // Update URL params while preserving existing params
        const newParams = new URLSearchParams(searchParams);
        
        const newIdString = String(newId);
        newParams.set(urlParamKey, newIdString);
        
        // Ensure other important params are preserved
        if (masterPoolId) newParams.set('mId', masterPoolId);
        if (gameId) newParams.set('gId', gameId);
        if (page) newParams.set('page', page);
        
        setSearchParams(newParams);
    };

    // Initialize URL if needed
    useEffect(() => {
        if (!searchParams.has(urlParamKey) && currentItem && currentItem[idKey] !== undefined && currentItem[idKey] !== null) {
            updateSelection(currentItem[idKey]);
        }
    }, []);

    // Handle dropdown selection change
    const handleSelectChange = (event) => {
        const newId = event.target.value;
        if (newId !== undefined && newId !== null) {
            updateSelection(newId);
            
            // Call external handler if provided
            if (onChange) {
                onChange(newId);
            }
        }
    };

    // Handle arrow navigation
    const handleArrowClick = (direction) => {
        if (!items || items.length === 0) return;
        
        let newIndex;
        
        if (direction === 'prev') {
            newIndex = Math.max(0, currentItemIndex - 1);
        } else {
            newIndex = Math.min(items.length - 1, currentItemIndex + 1);
        }
        
        const item = items[newIndex];
        if (item && item[idKey] !== undefined && item[idKey] !== null) {
            updateSelection(item[idKey]);
            
            // Call external handler if provided
            if (onChange) {
                onChange(item[idKey]);
            }
        }
    };

    if (!items || items.length === 0 || !currentItem || currentItem[idKey] === undefined || currentItem[idKey] === null) {
        return null;
    }

    // Calculate the width for the select container based on screen size
    const selectWidth = isMobile ? '180px' : '220px';

    return (
        <Box 
            className="my-picks-nav-bar"
            sx={{ 
                backgroundColor: backgroundColor,
                color: textColor,
            }}
        >
            <Box className="my-picks-nav-bar-container">
                <Box className="my-picks-nav-bar-content">
                    <IconButton 
                        onClick={() => handleArrowClick('prev')}
                        disabled={currentItemIndex === 0}
                    >
                        <ArrowBackIosNewIcon
                            className="my-picks-nav-bar-arrow-icon" 
                            sx={{ color: textColor }}
                        />
                    </IconButton>
                    <Box 
                        className="my-picks-nav-bar-select-container"
                        sx={{ 
                            width: selectWidth,
                            maxWidth: '100%'
                        }}
                    >
                        <Select
                            value={currentItem[idKey] !== undefined && currentItem[idKey] !== null ? currentItem[idKey] : ''}
                            onChange={handleSelectChange}
                            variant="outlined"
                            className="my-picks-nav-bar-select"
                            sx={{ 
                                color: textColor,
                                width: '100%',
                                '& .MuiSelect-select': {
                                    padding: '10px 14px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textAlign: 'left'
                                },
                                '& .MuiSvgIcon-root': {
                                    color: textColor
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                }
                            }}
                        >
                            {items?.map((item) => item && item[idKey] !== undefined && item[idKey] !== null ? (
                                <MenuItem 
                                    key={item[idKey]} 
                                    value={item[idKey]}
                                    sx={{ 
                                        color: '#000000',
                                        // Ensure menu items are left-aligned and have ellipsis on overflow
                                        padding: '6px 16px',
                                        '& .MuiTypography-root': {
                                            width: '100%',
                                            textAlign: 'left',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }
                                    }}
                                >
                                    {/* Wrap the text in Typography to better control the ellipsis */}
                                    <Typography noWrap>
                                        {item[nameKey] || "Unnamed"}
                                    </Typography>
                                </MenuItem>
                            ) : null)}
                        </Select>
                    </Box>
                    <IconButton 
                        onClick={() => handleArrowClick('next')}
                        disabled={currentItemIndex === items.length - 1}
                    >
                        <ArrowBackIosNewIcon 
                            className="my-picks-nav-bar-arrow-icon"
                            sx={{ color: textColor, transform: 'rotate(180deg)' }}
                        />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default SelectionBar;